import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AccountAssetsResult, type ApiError, type CaseAssetResult } from '@whinerp/client';
import { formatDate, todayIn } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/** Installed assets and their warranty (G-202): on the company, and on a case about one. */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';
const COVER_CLASS = { 'under-warranty': 'text-ink', expired: 'text-warning-text', none: 'text-ink-meta' } as const;

export function AccountAssetsPanel({ accountId }: { accountId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const key = ['account-assets', accountId];
  const assets = useQuery<AccountAssetsResult, ApiError>({ queryKey: key, queryFn: () => api.accountAssets({ path: { accountId } }) });
  const [draft, setDraft] = useState({ name: '', serial: '', location: '', installedOn: todayIn(format) });
  const add = useMutation({
    mutationFn: () => api.registerAsset({ path: { accountId }, body: { name: draft.name, serial: draft.serial || null, location: draft.location || null, installedOn: draft.installedOn } }),
    onSuccess: () => {
      setDraft({ name: '', serial: '', location: '', installedOn: todayIn(format) });
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
  if (!assets.data) return null;
  return (
    <Panel data-region="assets">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('installed.title')}</SectionLabel>
        <ul className="flex flex-col gap-1 text-dense">
          {assets.data.assets.map((asset) => (
            <li key={asset.assetId} data-asset={asset.assetId} className="flex flex-wrap gap-2">
              <span className="text-ink">{asset.name}</span>
              {asset.serial ? <span className="font-mono text-ink-600">{asset.serial}</span> : null}
              {asset.location ? <span className="text-ink-meta">{asset.location}</span> : null}
              <span className={`ms-auto ${COVER_CLASS[asset.cover]}`} data-asset-cover={asset.cover}>
                {asset.warrantyUntil ? t(`installed.cover.${asset.cover}`, { values: { date: formatDate(asset.warrantyUntil, format) } }) : t('installed.cover.none')}
              </span>
            </li>
          ))}
        </ul>
        <form className="flex flex-wrap items-end gap-2" data-asset-form onSubmit={(event) => { event.preventDefault(); add.mutate(); }}>
          <input className={INPUT} required maxLength={200} data-asset-name placeholder={t('installed.name')} value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
          <input className={INPUT} maxLength={120} data-asset-serial placeholder={t('installed.serial')} value={draft.serial} onChange={(event) => setDraft({ ...draft, serial: event.target.value })} />
          <input className={INPUT} maxLength={300} data-asset-location placeholder={t('installed.location')} value={draft.location} onChange={(event) => setDraft({ ...draft, location: event.target.value })} />
          <input className={INPUT} type="date" required data-asset-installed aria-label={t('installed.installed')} value={draft.installedOn} onChange={(event) => setDraft({ ...draft, installedOn: event.target.value })} />
          <Button size="small" type="submit" data-asset-add disabled={add.isPending}>{t('installed.register')}</Button>
        </form>
        {add.error ? <p className="text-dense text-danger-text" data-asset-error>{errorText(add.error as ApiError)}</p> : null}
      </div>
    </Panel>
  );
}

export function CaseAssetPanel({ caseId }: { caseId: string }): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const key = ['case-asset', caseId];
  const state = useQuery<CaseAssetResult, ApiError>({ queryKey: key, queryFn: () => api.caseAsset({ path: { caseId } }) });
  const set = useMutation({
    mutationFn: (assetId: string | null) => api.setCaseAsset({ path: { caseId }, body: { assetId } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
  if (!state.data || state.data.choices.length === 0) return null;
  const asset = state.data.asset;
  return (
    <Panel data-region="case-asset">
      <div className="flex flex-col gap-1">
        <SectionLabel>{t('installed.caseTitle')}</SectionLabel>
        <select className={INPUT} data-case-asset-select aria-label={t('installed.caseTitle')} value={asset?.assetId ?? ''} onChange={(event) => set.mutate(event.target.value || null)}>
          <option value="">{t('installed.noneChosen')}</option>
          {state.data.choices.map((choice) => <option key={choice.assetId} value={choice.assetId}>{choice.name}{choice.serial ? ` (${choice.serial})` : ''}</option>)}
        </select>
        {asset ? (
          <p className={`text-dense ${COVER_CLASS[asset.cover]}`} data-case-cover={asset.cover}>
            {asset.warrantyUntil ? t(`installed.cover.${asset.cover}`, { values: { date: formatDate(asset.warrantyUntil, format) } }) : t('installed.cover.none')}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}
